import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { resolvedEventBookingType, TDR } from 'tdr-common';
import { markSkipDefaultSearchParams } from '../../helpers/skipDefaultSearchParams';
import { useEventBookNow } from '../../hooks/useEventBookNow';
import Spinner from '../Spinner';

type EventBookNowButtonProps = {
	event: TDR.Event;
	restaurantSlug: string;
	tables: TDR.Table[];
	restaurantId: string;
	timezone?: string | null;
	className?: string;
	disabledClassName?: string;
	tabIndex?: number;
	onClick?: React.MouseEventHandler;
	enabled?: boolean;
	label?: string;
	noAvailabilityLabel?: string;
};

function toLocation(href: string): { pathname: string; search: string } {
	const q = href.indexOf('?');
	if (q === -1) {
		return { pathname: href, search: '' };
	}
	return { pathname: href.slice(0, q), search: href.slice(q) };
}

export const EventBookNowButton = ({
	event,
	restaurantSlug,
	tables,
	restaurantId,
	timezone,
	className,
	disabledClassName,
	tabIndex = 0,
	onClick,
	enabled = true,
	label,
	noAvailabilityLabel
}: EventBookNowButtonProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [clickedLoading, setClickedLoading] = useState(false);
	const isTableEvent = resolvedEventBookingType(event) === 'table' && !!event.linkedTableSlug;

	const {
		bookNowHref,
		isResolved,
		isLoading,
		hasNoAvailability
	} = useEventBookNow(event, restaurantSlug, tables, restaurantId, timezone, { enabled });

	const bookNowText = label ?? t('actions.book_now', { defaultValue: 'Book Now' });
	const noAvailabilityText = noAvailabilityLabel ?? t('inventory.sold_out', { defaultValue: 'Sold out' });

	const navigateToHref = (href: string) => {
		if (isTableEvent) {
			markSkipDefaultSearchParams();
		}
		navigate(toLocation(href));
	};

	useEffect(() => {
		if (!clickedLoading || !isResolved) {
			return;
		}
		setClickedLoading(false);
		if (bookNowHref) {
			navigateToHref(bookNowHref);
		}
	}, [clickedLoading, isResolved, bookNowHref, navigate, isTableEvent]);

	if (!enabled) {
		return null;
	}

	if (isResolved && hasNoAvailability) {
		return (
			<button
				className={disabledClassName ?? className}
				type="button"
				disabled
				tabIndex={tabIndex}
				aria-disabled="true"
			>
				{noAvailabilityText}
			</button>
		);
	}

	if (clickedLoading && isLoading) {
		return (
			<button
				className={className}
				type="button"
				disabled
				tabIndex={tabIndex}
				aria-busy="true"
				aria-label={bookNowText}
			>
				<Spinner size="xs" />
			</button>
		);
	}

	if (bookNowHref) {
		const handleLinkClick = (e: React.MouseEvent) => {
			if (isTableEvent) {
				markSkipDefaultSearchParams();
			}
			onClick?.(e);
		};

		return (
			<Link
				className={className}
				to={toLocation(bookNowHref)}
				tabIndex={tabIndex}
				onClick={handleLinkClick}
			>
				{bookNowText}
			</Link>
		);
	}

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		onClick?.(e);
		if (e.defaultPrevented) {
			return;
		}
		setClickedLoading(true);
	};

	return (
		<button
			className={className}
			type="button"
			tabIndex={tabIndex}
			onClick={handleClick}
		>
			{bookNowText}
		</button>
	);
};
